/**
 * OS/browser notifications that mirror the timeoutBeeps cues.
 *
 *   - Warning (~70% elapsed)
 *   - Urgent  (~90% elapsed)
 *   - Final   (100%, fired from the auto-stop handler)
 *
 * Uses the Web Notifications API — delivered as native OS toasts on
 * macOS / Windows / Linux. Requires explicit user permission; all helpers
 * degrade silently to no-ops if permission is missing or the API isn't
 * available (audio + visual cues still work).
 */

export const isNotificationSupported = () =>
  typeof window !== 'undefined' && 'Notification' in window;

/**
 * 'granted' | 'denied' | 'default' | 'unsupported'
 */
export const getNotificationPermission = () => {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
};

/**
 * Request permission if it hasn't been granted or denied yet.
 * Returns the resulting permission string ('granted' | 'denied' | 'default' | 'unsupported').
 */
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) return 'unsupported';
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch (e) {
    return Notification.permission;
  }
};

const show = (title, body) => {
  if (getNotificationPermission() !== 'granted') return;
  try {
    const n = new Notification(title, {
      body,
      // Tag lets repeated notifications collapse in the OS tray instead of
      // stacking a backlog.
      tag: 'snaptest-question-timer',
      renotify: true,
    });
    n.onclick = () => {
      try {
        window.focus();
      } catch (e) {
        /* ignore */
      }
      n.close();
    };
  } catch (e) {
    // Some browsers (notably mobile Chrome) restrict non-service-worker
    // notifications — fail silently.
  }
};

export const showWarningNotification = (title, body) => show(title, body);
export const showUrgentNotification = (title, body) => show(title, body);
export const showFinalNotification = (title, body) => show(title, body);

/**
 * Schedule the warning / urgent notifications at 70% and 90% of `totalMs`.
 * The final notification is fired directly by the auto-stop handler so it's
 * not silenced by cancel(). `messages` shape:
 *   { warningTitle, warningBody, urgentTitle, urgentBody }
 * Returns { cancel } to clear the pending timeouts on manual submit / unmount.
 */
export const scheduleTimeoutNotifications = (totalMs, messages = {}) => {
  const warningAt = Math.floor(totalMs * 0.7);
  const urgentAt = Math.floor(totalMs * 0.9);

  const warningId = warningAt > 0
    ? setTimeout(
        () => showWarningNotification(messages.warningTitle || '', messages.warningBody || ''),
        warningAt,
      )
    : null;
  const urgentId = urgentAt > 0
    ? setTimeout(
        () => showUrgentNotification(messages.urgentTitle || '', messages.urgentBody || ''),
        urgentAt,
      )
    : null;

  return {
    cancel: () => {
      if (warningId) clearTimeout(warningId);
      if (urgentId) clearTimeout(urgentId);
    },
  };
};
